import { useEffect, useRef, useState } from "react";
import { useLanguage } from "@/lib/i18n";

const slug = (o) => o.toLowerCase().replace(/\s+/g, "-");

const MultiSearchSelect = ({
  options,
  pinnedOptions = [],
  value,
  onChange,
  placeholder,
  testId,
  allowAdd = false,
  inlineChips = false,
  cleanChips = false,
}) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open]);

  const selected = value || [];
  const toggle = (o) =>
    onChange(selected.includes(o) ? selected.filter((v) => v !== o) : [...selected, o]);

  const q = query.trim();
  const canAdd =
    allowAdd &&
    q &&
    ![...options, ...pinnedOptions, ...selected].some((o) => o.toLowerCase() === q.toLowerCase());
  const addCustom = () => {
    if (!selected.includes(q)) onChange([...selected, q]);
    setQuery("");
  };

  const filtered = [
    ...options.filter((o) => o.toLowerCase().includes(q.toLowerCase())),
    ...pinnedOptions.filter((o) => !options.includes(o)),
  ];

  const caret = (
    <svg
      className="fm-combo-caret"
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3 6l5 5 5-5"
        stroke="#C5A059"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const toggleMenu = () => {
    setOpen((v) => !v);
    setQuery("");
  };

  return (
    <div className="fm-combo" ref={wrapRef}>
      {inlineChips ? (
        <div
          className={`fm-input fm-combo-trigger fm-combo-trigger-chips${cleanChips ? " fm-combo-chips-clean" : ""}`}
          role="button"
          tabIndex={0}
          aria-haspopup="listbox"
          aria-expanded={open}
          data-testid={testId}
          onClick={toggleMenu}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleMenu();
            }
          }}
        >
          {selected.length === 0 && (
            <span className="fm-combo-placeholder">{t(placeholder)}</span>
          )}
          {selected.map((o, i) => (
            <span key={o} className="cp-plain-item">
              {o}
              <button
                type="button"
                aria-label={`Remove ${o}`}
                data-testid={`${testId}-chip-remove-${slug(o)}`}
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(o);
                }}
              >
                ×
              </button>
              {i < selected.length - 1 && <span className="cp-plain-sep">,</span>}
            </span>
          ))}
          {caret}
        </div>
      ) : (
        <button
          type="button"
          className="fm-input fm-combo-trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          data-testid={testId}
          onClick={toggleMenu}
        >
          <span className={selected.length ? "fm-combo-value" : "fm-combo-placeholder"}>
            {selected.length ? `${selected.length} ${t("selected")}` : t(placeholder)}
          </span>
          {caret}
        </button>
      )}

      {open && (
        <div className="fm-combo-menu">
          <input
            autoFocus
            type="text"
            className="fm-combo-search"
            placeholder={t("Type to search…")}
            value={query}
            data-testid={`${testId}-search`}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
          />
          <ul className="fm-combo-list" role="listbox" aria-multiselectable="true">
            {filtered.map((o) => (
              <li
                key={o}
                role="option"
                aria-selected={selected.includes(o)}
                className={`fm-combo-option${selected.includes(o) ? " is-selected" : ""}`}
                data-testid={`${testId}-option-${slug(o)}`}
                onClick={() => toggle(o)}
              >
                {o}
                {selected.includes(o) && (
                  <span className="cp-multi-check" aria-hidden="true">
                    ✓
                  </span>
                )}
              </li>
            ))}
            {canAdd && (
              <li
                className="fm-combo-option fm-combo-add"
                data-testid={`${testId}-add`}
                onClick={addCustom}
              >
                + Add “{q}”
              </li>
            )}
            {filtered.length === 0 && !canAdd && (
              <li className="fm-combo-empty">{t("No matches found")}</li>
            )}
          </ul>
        </div>
      )}

      {selected.length > 0 && !inlineChips && (
        <div className="cp-multi-chips" data-testid={`${testId}-chips`}>
          {selected.map((o) => (
            <span key={o} className="cp-multi-chip">
              {o}
              <button
                type="button"
                aria-label={`Remove ${o}`}
                data-testid={`${testId}-chip-remove-${slug(o)}`}
                onClick={() => toggle(o)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiSearchSelect;
